import { useEffect, useRef, useState } from "react";

export function useMultiselectEntity({
  allEntities = [],
  initialSelect = [],
  isSelectAllOnLoadMore = false,
}) {
  const [selected, setSelected] = useState(initialSelect);
  const [entities, setEntities] = useState(allEntities);
  const previousEntities = useRef(allEntities);
  const isSelectAllActive = useRef(false);

  useEffect(() => {
    if (!isSelectAllOnLoadMore) {
      previousEntities.current = allEntities;
      return;
    }

    setEntities((currentEntities) => {
      const isUnchanged =
        currentEntities.length === allEntities.length &&
        currentEntities.every((entity, index) => entity === allEntities[index]);

      return isUnchanged ? currentEntities : allEntities;
    });

    const haveEntitiesChanged =
      previousEntities.current.length !== allEntities.length ||
      previousEntities.current.some(
        (entity, index) => entity !== allEntities[index],
      );
    const isLoadMore =
      haveEntitiesChanged &&
      previousEntities.current.length < allEntities.length &&
      previousEntities.current.every(
        (entity, index) => entity === allEntities[index],
      );

    if (isSelectAllActive.current && isLoadMore) {
      setSelected(allEntities);
    } else if (haveEntitiesChanged) {
      isSelectAllActive.current = false;
    }

    previousEntities.current = allEntities;
  }, [allEntities, isSelectAllOnLoadMore]);

  const isSelectedPartial = () => {
    return selected.length < entities.length && selected.length !== 0;
  };

  const isSelectedAll = () => {
    return selected.length > 0 && selected.length === allEntities.length;
  };

  const isSelected = (entity) => {
    return selected.includes(entity);
  };

  const onRemoveOne = (entity) => {
    isSelectAllActive.current = false;
    setSelected((prev) => prev.filter((i) => i !== entity));
  };

  const onToggleOne = (entity) => {
    isSelectAllActive.current = false;
    setSelected((prev) =>
      isSelected(entity)
        ? prev.filter((item) => item !== entity)
        : [...prev, entity],
    );
  };

  const onToggleAll = () => {
    const shouldSelectAll = !isSelectedAll();
    isSelectAllActive.current = shouldSelectAll;
    setSelected(shouldSelectAll ? allEntities : []);
  };

  const resetSelected = () => {
    isSelectAllActive.current = false;
    setSelected([]);
  };

  const updateSelected = (value) => {
    isSelectAllActive.current = false;
    setSelected(value);
  };

  return {
    onToggleOne,
    onToggleAll,
    onRemoveOne,
    isSelected,
    isSelectedAll,
    isSelectedPartial,
    resetSelected,
    selected,
    setSelected: updateSelected,
    setEntities,
  };
}
